import { Routes } from '@angular/router';
import { canAccessPhoenix, canAdministerCompany } from './core/auth/phoenix-access';
import { phoenixUnsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { ENVIRONMENT_WORKSPACE_ROUTES } from './environments/environment-workspace.routes';
import { ONBOARDING_SITE_ROUTE_PARAM } from './onboarding/onboarding.util';
import { ShellLayoutComponent } from './shell-layout.component';

/**
 * Phoenix-Portal routing skeleton (PBI 1467 + Task 1477 + PBI 1537 + the Backup Environments redesign).
 *
 *   /                          → landing (PUBLIC, full-bleed marketing page — NO shell chrome)
 *   /onboarding                → customer setup page, all sites (canAccessPhoenix gate)
 *   /onboarding/:phoenixSiteId → the same page focused on ONE site's journey  [AB#2274]
 *   /board                     → status board (canAccessPhoenix gate)  [Task 1477]
 *   /runs                      → run list/detail drill-in (canAccessPhoenix gate)
 *   /backup-environments              → Backup Environments home (agent + bound-config cards)
 *   /backup-environments/:agentId     → Backup Environment WORKSPACE shell → redirects to …/overview
 *   /backup-environments/:agentId/overview | export | recovery | configuration → the four sections
 *   /backup-environments/:agentId/configuration/observed → configured-vs-observed drift view
 *   /backup-environments/:agentId/observed → legacy redirect → …/configuration/observed
 *   /settings                  → company profile (canAccessPhoenix + CompanyAdmin)
 *   /no-access                 → shown when authenticated but unentitled
 *   /agents                    → legacy redirect → /backup-environments
 *
 * The Backup Environments redesign makes config 1:1 PERMANENTLY bound to an agent — a
 * Backup Environment = agent + its one config, created together. The standalone config-list and
 * config-create entries are retired; the typed config-authoring FORM survives, hosted inside
 * the Backup Environment detail. The standalone /installer-download page is ALSO retired (it called
 * the config-less customer installer endpoint that minted orphan agents), and so is the
 * "Add environment" wizard (Kevin 2026-08-06, AB#2275 direction): customers do NOT create
 * Backup Environments manually any more — a Backup Environment comes into existence through ordering a
 * Phoenix Site (the setup flow). Installer downloads happen via the per-Backup-Environment link issue /
 * re-issue on the detail page. The authenticated areas render inside the ShellLayout frame;
 * the landing deliberately sits OUTSIDE {@link ShellLayoutComponent} (edge-to-edge).
 *
 * Pages are lazy-loaded standalone components.
 */
export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('./landing/landing-page.component').then((m) => m.LandingPageComponent),
    data: { title: 'phoenix.landing.title' },
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./login/login-page.component').then((m) => m.LoginPageComponent),
    data: { title: 'phoenix.login.title' },
  },
  {
    // B2C redirect-back target (ADO #1466) — PUBLIC, outside the shell + guard: exchanges
    // the authorization code, lands the session, then navigates to the stashed returnUrl.
    path: 'auth-callback',
    loadComponent: () =>
      import('./auth/auth-callback.component').then((m) => m.AuthCallbackComponent),
    data: { title: 'phoenix.authCallback.title' },
  },
  // ----- Legal pages (GDPR/TMG reachability) — PUBLIC, outside the shell, like the main
  // portal's /impressum, /privacy-consent and /legal-consent. The CONSENT itself is NOT a
  // route: it is enforced as a blocking dialog by ShellLayoutComponent (mirroring the main
  // portal's MainLayout gate), so these pages stay freely readable pre-login.
  {
    path: 'impressum',
    loadComponent: () =>
      import('./legal/impressum-page.component').then((m) => m.ImpressumPageComponent),
    data: { title: 'impressum.title' },
  },
  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./legal/privacy-policy-page.component').then((m) => m.PrivacyPolicyPageComponent),
    data: { title: 'privacyPolicy.title' },
  },
  {
    path: 'terms',
    loadComponent: () =>
      import('./legal/terms-page.component').then((m) => m.TermsPageComponent),
    data: { title: 'legalConsent.agb.title' },
  },
  {
    path: '',
    component: ShellLayoutComponent,
    children: [
      {
        // Onboarding — the customer-facing progress stepper (#1997): what the tenant must do + how far
        // MakroFactory has come with provisioning. Read-only.
        path: 'onboarding',
        canActivate: [canAccessPhoenix],
        loadComponent: () =>
          import('./onboarding/onboarding-page.component').then((m) => m.OnboardingPageComponent),
        data: { title: 'phoenix.onboarding.title' },
      },
      {
        // Site-ADDRESSED setup (AB#2274) — the SAME page, focused on one Phoenix site's journey; the
        // deep-link target of the per-environment setup links. An unknown/foreign id is not a 404: the
        // page falls back to the overview and says the address did not resolve, so a stale link (e.g.
        // to a decommissioned site) still lands somewhere useful.
        path: `onboarding/:${ONBOARDING_SITE_ROUTE_PARAM}`,
        canActivate: [canAccessPhoenix],
        loadComponent: () =>
          import('./onboarding/onboarding-page.component').then((m) => m.OnboardingPageComponent),
        data: { title: 'phoenix.onboarding.title' },
      },
      {
        path: 'board',
        canActivate: [canAccessPhoenix],
        loadComponent: () =>
          import('./board/board-page.component').then((m) => m.BoardPageComponent),
        data: { title: 'phoenix.board.title' },
      },
      {
        path: 'runs',
        canActivate: [canAccessPhoenix],
        loadComponent: () =>
          import('./board/run-list-page.component').then((m) => m.RunListPageComponent),
        data: { title: 'phoenix.runs.title' },
      },
      {
        // Backup Environments home — the agents surface IS the backup-environments list now
        // (each card = agent + its 1:1 bound config). No manual create action: a
        // Backup Environment is provided through the Phoenix Site order / setup flow.
        path: 'backup-environments',
        canActivate: [canAccessPhoenix],
        loadComponent: () =>
          import('./agents/agents-page.component').then((m) => m.AgentsPageComponent),
        data: { title: 'phoenix.environments.title' },
      },
      {
        // ONE BACKUP ENVIRONMENT = a routed WORKSPACE (AB#2499): a persistent shell (header, section tab bar,
        // lifecycle band) around four addressable sections. The SHELL COMPONENT provides the workspace
        // store, so exactly one instance of the environment's state — and of its three polls, the
        // one-shot installer link and any running configuration test — lives from entering the
        // environment until leaving it. A switch between sections never touches it, because activating a
        // sibling child does not re-create the parent component.
        //
        // Deliberately NOT a route-level `providers` array: Angular 21 keeps a route injector for the
        // whole application lifetime unless the experimental `withExperimentalAutoCleanupInjectors()` is
        // enabled, so the polls would outlive the visit (see the shell component's class doc).
        //
        // The leave guard deliberately sits on the CHILD that hosts the config form, not here: a parent
        // canDeactivate does not fire on a sibling switch (see ENVIRONMENT_WORKSPACE_ROUTES).
        path: 'backup-environments/:agentId',
        canActivate: [canAccessPhoenix],
        loadComponent: () =>
          import('./environments/environment-shell.component').then(
            (m) => m.EnvironmentShellComponent
          ),
        data: { title: 'phoenix.environments.detail.title' },
        children: ENVIRONMENT_WORKSPACE_ROUTES,
      },
      {
        // Subscription — the tenant's Stripe subscription overview + Customer-Portal hand-off.
        // CompanyAdmin only, like /settings: the portal-session endpoint is Billing.Manage-gated.
        // Per-license cancels stay OFF this page (Phoenix units are site-bound, AB#2107).
        path: 'subscription',
        canActivate: [canAccessPhoenix, canAdministerCompany],
        loadComponent: () =>
          import('./subscription/subscription-page.component').then(
            (m) => m.SubscriptionPageComponent
          ),
        data: { title: 'phoenix.subscription.title' },
      },
      {
        // Company settings — the tenant's own organizational master data, editable without a
        // detour into the BCM portal. CompanyAdmin only: the underlying PUT is gated
        // `Tenants.Edit`, which no Phoenix role carries (see canAdministerCompany).
        path: 'settings',
        canActivate: [canAccessPhoenix, canAdministerCompany],
        canDeactivate: [phoenixUnsavedChangesGuard],
        loadComponent: () =>
          import('./settings/company-settings-page.component').then(
            (m) => m.CompanySettingsPageComponent
          ),
        data: { title: 'phoenix.settings.title' },
      },
      // Legacy redirect: the old /agents entry now lands on /backup-environments.
      { path: 'agents', pathMatch: 'full', redirectTo: 'backup-environments' },
      // Legacy redirect: the retired /installer-download page now lands on /backup-environments
      // (downloads happen via the per-env installer-link issue / re-issue).
      { path: 'installer-download', pathMatch: 'full', redirectTo: 'backup-environments' },
      {
        path: 'no-access',
        loadComponent: () =>
          import('./landing/no-access-page.component').then((m) => m.NoAccessPageComponent),
        // This is the sole authenticated route that deliberately remains visible before the
        // tenant-wide consent check: an unentitled user cannot accept that consent and would
        // otherwise be trapped behind its blocking dialog (AB#2709).
        data: { title: 'phoenix.noAccess.title', skipPrivacyConsent: true },
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
